'use client';

import { type ReactNode, useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

/**
 * Scroll-linked parallax. Translates its children on the Y axis based on how
 * far the element is through the viewport, so layers with different `speed`
 * values drift at different rates and read as depth. Gated on
 * prefers-reduced-motion — those users get no transform at all.
 *
 * `speed` is roughly the peak travel in px ÷ 100 (0.4 ≈ ±40px). Positive drifts
 * down as you scroll up; negative drifts the opposite way.
 */
export function Parallax({
  children,
  speed = 0.3,
  className,
}: {
  children: ReactNode;
  speed?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: no-preference)');

    if (!mq.matches) return;

    const el = ref.current;

    if (!el) return;

    let raf = 0;

    const update = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // -1 when just below the viewport, +1 when just above; 0 at center.
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);

      el.style.transform = `translate3d(0, ${progress * speed * 100}px, 0)`;
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);

      if (raf) cancelAnimationFrame(raf);
    };
  }, [speed]);

  return (
    <div ref={ref} className={cn('will-change-transform', className)}>
      {children}
    </div>
  );
}
